import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Send, Users } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MembershipBadge } from "@/components/MembershipBadge";
import { FollowButton } from "@/components/social/FollowButton";

function Avatar({ url, name, size = 44 }) {
    const initial = (name || "?").charAt(0).toUpperCase();
    if (url) {
        return <img src={url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />;
    }
    return (
        <div className="rounded-full bg-[var(--ok-accent)] text-black font-display font-black flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.4 }}>{initial}</div>
    );
}

const FILTERS = [
    { k: "all", label: "All members" },
    { k: "newest", label: "Newest" },
    { k: "research_council", label: "Research Council" },
    { k: "verified_witness", label: "Verified Witness" },
    { k: "following", label: "Following", auth: true },
    { k: "followers", label: "Followers", auth: true },
];

/** Member directory — every real account, searchable + filterable. */
export function MemberDirectory() {
    const { user } = useAuth();
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const timer = useRef(null);

    useEffect(() => {
        clearTimeout(timer.current);
        setLoading(true);
        timer.current = setTimeout(() => {
            api.get("/social/members", { params: { q, filter, limit: 200 } })
                .then((r) => { setItems(r.data?.items || []); setTotal(r.data?.total || 0); })
                .catch(() => { setItems([]); setTotal(0); })
                .finally(() => setLoading(false));
        }, q ? 250 : 0);
        return () => clearTimeout(timer.current);
    }, [q, filter]);

    return (
        <div className="mt-6" data-testid="member-directory">
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-40 pointer-events-none" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search members by name…"
                    className="ok-input w-full text-sm pl-9"
                    data-testid="member-search-input"
                />
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap" data-testid="member-filters">
                {FILTERS.filter((f) => !f.auth || user).map((f) => (
                    <button
                        key={f.k}
                        onClick={() => setFilter(f.k)}
                        className={`px-3 py-1.5 rounded-sm font-mono uppercase text-[10px] tracking-widest border transition ${filter === f.k ? "bg-[var(--ok-accent)] text-black border-[var(--ok-accent)]" : "opacity-70 hover:opacity-100 border-[var(--ok-border)]"}`}
                        data-testid={`member-filter-${f.k}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-widest opacity-50 flex items-center gap-2" data-testid="member-count">
                <Users className="w-3 h-3" /> {loading ? "Loading…" : `${total} member${total === 1 ? "" : "s"}`}
            </div>
            <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="member-grid">
                {items.map((m) => (
                    <div key={m.id} className="ok-card p-4 flex items-start gap-3" data-testid={`member-card-${m.id}`}>
                        <Link to={`/u/${m.id}`}><Avatar url={m.avatar_url} name={m.name} /></Link>
                        <div className="flex-1 min-w-0">
                            <Link to={`/u/${m.id}`} className="text-sm font-medium hover:text-[var(--ok-accent)] flex items-center gap-2" data-testid={`member-name-${m.id}`}>
                                <span className="truncate">{m.name}</span>
                                {m.subscription_tier === "research_council" && <MembershipBadge tier="research_council" size="sm" />}
                                {m.subscription_tier === "verified_witness" && <MembershipBadge tier="verified_witness" size="sm" />}
                            </Link>
                            {m.bio && <div className="text-xs opacity-60 line-clamp-2 mt-0.5">{m.bio.split("\n")[0]}</div>}
                            <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mt-1.5">
                                {m.follower_count} follower{m.follower_count === 1 ? "" : "s"} · rep {m.reputation}
                            </div>
                            <div className="flex items-center gap-1.5 mt-2">
                                <FollowButton userId={m.id} initialFollowing={m.is_following} />
                                {user && user.id !== m.id && (
                                    <Link to={`/messages/${m.id}`} className="ok-btn-outline text-xs inline-flex items-center gap-1" data-testid={`member-message-${m.id}`}>
                                        <Send className="w-3 h-3" /> Message
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {!loading && items.length === 0 && (
                <div className="ok-card p-6 text-center text-sm opacity-70 mt-3" data-testid="member-empty">No members match.</div>
            )}
        </div>
    );
}
